import { zipObject, zip, isEqual, get, sortBy } from 'lodash';
import {
  getSingleResult,
  performanceTierStacks,
  isRubric,
  isFRQ,
  getScoreTimeSpents,
  averageTimeSpentInSeconds,
  secondsToTime,
} from 'legacy-src/utility/progressHelpers';
import {
  questionTiers,
  questionFRQTiers,
} from 'legacy-src/components/assessment/Results/PerformanceBarPopover';
import { QuestionTypes } from 'legacy/packages/unit-outline/src/constants';

export const getPerformanceStacks = (itemId, report, tiers, students) =>
  performanceTierStacks(
    students && students.length,
    report,
    tiers,
    students,
    itemId,
  );

export const getQuestionSkills = (question, formatted = true) => {
  if (!question.relatedSkills) {
    return;
  }
  const relatedSkillCategory = question.relatedSkills.find((skill) =>
    skill.title.match(/Skill|Practice/),
  );
  if (
    !relatedSkillCategory ||
    !relatedSkillCategory.children ||
    !relatedSkillCategory.children.length
  ) {
    return;
  }
  return formatted
    ? relatedSkillCategory.children.reduce(
        (accum, current) => [
          ...accum,
          {
            name: current.description,
            category: current.category,
            label: current.label.split(' ')[1],
          },
        ],
        [],
      )
    : relatedSkillCategory.children;
};

export const getQuestionUnits = (question) => {
  if (!question.relatedOtherTags) {
    return;
  }
  const relatedOtherTags = question.relatedOtherTags.filter((tag) =>
    tag.category.match(/Unit/),
  );
  if (!relatedOtherTags) {
    return;
  }
  return relatedOtherTags;
};

export const getQuestionTopics = (question, isOtherPart = false) => {
  if (!question.relatedOtherTags && !question.relatedSkills) {
    return;
  }
  let topics;
  if (isOtherPart) {
    if (!question.relatedSkills) return;
    topics = question.relatedSkills
      .filter((tag) => tag.title.match(/Topic/))
      .map((tag) => tag.children[0]);
  } else {
    if (!question.relatedOtherTags) return;
    topics = question.relatedOtherTags.filter((tag) =>
      tag.category.match(/Topic/),
    );
  }
  return topics;
};

export const getQuestionTaskModels = (question) => {
  if (!question.relatedOtherTags) {
    return;
  }
  const relatedOtherTags = question.relatedOtherTags.filter((tag) =>
    tag.category.match(/Task Model/),
  );
  if (!relatedOtherTags) {
    return;
  }
  return relatedOtherTags;
};

export const getQuestionProficiencies = (question) => {
  if (!question.relatedOtherTags) {
    return;
  }
  const relatedOtherTags = question.relatedOtherTags.filter((tag) =>
    tag.category.match(/Proficiencies/),
  );
  if (!relatedOtherTags) {
    return;
  }
  return relatedOtherTags;
};

export const getStudentMarksAndColor = ({
  currentStudentId,
  showSingleResults,
  learnosityItemReference,
  report,
}) => {
  const singleResult =
    (showSingleResults &&
      getSingleResult([currentStudentId], learnosityItemReference, report)) ||
    {};
  const { learnosityReportScoreParsed, learnosityReportMaxScore } =
    singleResult;
  const textColor = learnosityReportScoreParsed
    ? learnosityReportScoreParsed / learnosityReportMaxScore === 1
      ? 'text-green-500'
      : 'text-yellow-900'
    : 'text-red-500';
  return {
    learnosityReportScoreParsed,
    learnosityReportMaxScore,
    textColor,
  };
};

export const calculateResponseDistribution = (
  studentResponses = {},
  sessionTemplate = { items: [] },
  learnosityItemReference,
  studentId = null,
) => {
  // Calculate the response distribution for a given MCQ question
  // StudentId is an optional parameter to get the response for a specific student, it is used to get the isCorrect, studentResponse values.
  // if studentId is not provided, the function will return null for those keys
  const { items: sessionTemplateItems } = sessionTemplate;
  const questionInfo = sessionTemplateItems.find(
    (sessionTemplateItem) =>
      sessionTemplateItem.reference === learnosityItemReference,
  );
  if (!questionInfo) return null;

  const { questions } = questionInfo;
  const [question] = questions;
  const questionType = question?.type;

  if (questionType !== 'mcq') {
    return { questionType };
  }

  const {
    options,
    validation: {
      valid_response: { value },
    },
  } = question;
  const studentResponse = studentId
    ? studentResponses[studentId]
      ? studentResponses[studentId][learnosityItemReference]
      : null
    : null;
  const [validResponse] = value;
  const optionsLabels = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H']; // MCQ Questions usually have between 2-5 options
  const optionsToGenerate = optionsLabels.slice(0, options.length);
  const rightAnswerIdx = options.findIndex(
    ({ value: rightValue }) => rightValue === validResponse,
  );
  const rightAnswer = optionsLabels[rightAnswerIdx];
  const responseDistribution = {};
  optionsToGenerate.forEach((option) => {
    responseDistribution[option] = 0;
  });
  responseDistribution['—'] = 0;

  Object.keys(studentResponses).forEach((stId) => {
    const response = studentResponses[stId][learnosityItemReference];
    if (response === undefined) {
      responseDistribution['—'] += 1;
    } else {
      responseDistribution[response[0]] += 1;
    }
  });

  const mostCommonDistractors = () => {
    const wrongAnswers = Object.keys(responseDistribution).filter(
      (key) => key !== rightAnswer && key !== '—',
    );

    const wrongAnswersDict = wrongAnswers.reduce((accum, key) => {
      accum[key] = responseDistribution[key];
      return accum;
    }, {});

    const maxResponses = Math.max(
      ...Object.values(wrongAnswersDict).filter((count) => count !== 0),
    );
    const keysWithMostResponses = Object.keys(wrongAnswersDict).filter(
      (key) => responseDistribution[key] === maxResponses,
    );
    return keysWithMostResponses.slice(0, 2); // Return the first two most common distractors, excluding the right answer and the '—' (No answer) option
  };

  return {
    responseDistribution,
    rightAnswer,
    studentResponse,
    isCorrect: studentResponse ? studentResponse[0] === rightAnswer : null,
    mostCommonDistractors: mostCommonDistractors(),
    questionType,
  };
};

export const getReportToCheckAndTiers = ({
  item,
  learnosityReportsByRubricData,
  learnosityReportsStudentScoring,
  learnosityReportsByItemData,
}) => {
  const isRubricItem = isRubric(item);
  const isFRQItem = isFRQ(item);
  const reportToCheck = isRubricItem
    ? learnosityReportsByRubricData.length
      ? learnosityReportsByRubricData
      : learnosityReportsStudentScoring
    : learnosityReportsByItemData;

  const currentTiers =
    isFRQItem || isRubricItem ? questionFRQTiers : questionTiers;
  return [reportToCheck, currentTiers];
};

export const normalizeQuestionsTableData = (items, otherArgs) => {
  const { averageTimeSpents, userTimeSpentPerQuestion } = getScoreTimeSpents(
    otherArgs.learnosityReportsByItemData,
  );
  return items.reduce((itemAccum, item) => {
    const {
      assignment: { students: assignmentStudents },
      students,
      studentId,
      currentStudentId,
      showSingleResults,
      isStudent,
      sortBy,
      isOtherPart,
    } = otherArgs;
    const availableStudents = assignmentStudents || students;

    const [report, tiers] = getReportToCheckAndTiers({ item, ...otherArgs });
    const { learnosityItemReference, __typename } = item;

    const isMCQQuestion = __typename === QuestionTypes.MULTIPLE_CHOICE_QUESTION;

    const stacks = getPerformanceStacks(
      learnosityItemReference,
      report,
      tiers,
      availableStudents,
    );

    const skills = getQuestionSkills(item);
    const units = getQuestionUnits(item);
    const topics = getQuestionTopics(item, isOtherPart);
    const taskModels = getQuestionTaskModels(item);
    const proficiencies = getQuestionProficiencies(item);

    // sort the tag list within the column base on sorting criteria
    if (sortBy.length) {
      const [sortInfo] = sortBy;
      const { id: sortId, desc: isDescending } = sortInfo;
      if (sortId === 'topics') {
        // need to compare the string by topic label number, for example,
        // 2.1 should be greater than 1.3, 1.10 should be greater than 1.1
        topics &&
          topics.sort((aTag, bTag) => {
            const aTagData = aTag.label.split('.');
            const bTagData = bTag.label.split('.');
            if (parseInt(aTagData[0]) > parseInt(bTagData[0])) {
              return isDescending ? -1 : 1;
            } else if (parseInt(aTagData[0]) < parseInt(bTagData[0])) {
              return isDescending ? 1 : -1;
            } else if (parseInt(aTagData[1]) > parseInt(bTagData[1])) {
              return isDescending ? -1 : 1;
            }
            return isDescending ? 1 : -1;
          });
      } else if (sortId === 'units') {
        units &&
          units.sort((aTag, bTag) => {
            if (aTag.label > bTag.label) {
              return isDescending ? -1 : 1;
            }
            return isDescending ? 1 : -1;
          });
      } else if (sortId === 'taskModels') {
        taskModels &&
          taskModels.sort((aTag, bTag) => {
            if (aTag.label > bTag.label) {
              return isDescending ? -1 : 1;
            }
            return isDescending ? 1 : -1;
          });
      } else if (sortId === 'skills') {
        skills &&
          skills.sort((aTag, bTag) => {
            if (aTag.label > bTag.label) {
              return isDescending ? -1 : 1;
            }
            return isDescending ? 1 : -1;
          });
      }
    }

    const { learnosityReportScoreParsed, learnosityReportMaxScore, textColor } =
      getStudentMarksAndColor({
        currentStudentId,
        showSingleResults,
        learnosityItemReference,
        report,
      });

    const {
      studentResponses,
      sessionTemplate,
      otherPartStudentResponses,
      otherPartSessionTemplate,
    } = otherArgs;

    const studentResponsesObj = isOtherPart
      ? otherPartStudentResponses
      : studentResponses;
    const sessionTemplateObj = isOtherPart
      ? otherPartSessionTemplate
      : sessionTemplate;

    const ats = averageTimeSpentInSeconds(
      averageTimeSpents[learnosityItemReference],
    );
    const averageTimeSpent = secondsToTime(ats);
    const userTimeSpent = () => {
      let timeSpent;
      if (
        userTimeSpentPerQuestion &&
        userTimeSpentPerQuestion[currentStudentId] &&
        userTimeSpentPerQuestion[currentStudentId][learnosityItemReference] !==
          undefined
      ) {
        timeSpent =
          userTimeSpentPerQuestion[currentStudentId][learnosityItemReference];
      } else {
        // Handle the case where the data is not available
        timeSpent = null;
      }
      return timeSpent;
    };

    const userTimeSpentInSeconds = secondsToTime(userTimeSpent());

    const responseDistribution =
      isMCQQuestion && !isStudent
        ? calculateResponseDistribution(
            studentResponsesObj,
            sessionTemplateObj,
            learnosityItemReference,
            studentId,
          )
        : null;

    const response = isMCQQuestion ? responseDistribution : null;

    return [
      ...itemAccum,
      {
        question: [item.id, `${item.idx}*${item.title}`],
        performance: stacks,
        skills,
        averageTimeSpent,
        units,
        topics,
        taskModels,
        proficiencies,
        children: item.children,
        otherArgs,
        learnosityReportScoreParsed,
        learnosityReportMaxScore,
        textColor,
        showSingleResults,
        currentStudentId,
        availableStudents,
        learnosityItemReference,
        averageTimeSpentInSeconds: ats,
        userTimeSpentInSeconds,
        responseDistribution,
        response,
      },
    ];
  }, []);
};

const getFullCreditNumber = (stacks) => {
  const fullCreditStack = stacks.find(
    (stack) => stack.label === 'Full Credit' || stack.label === 'Correct',
  );
  return fullCreditStack ? fullCreditStack.students.length : 0;
};

const getPartialCreditNumber = (stacks) => {
  const partialCredit = stacks.find(
    (stack) => stack.label === 'Partial Credit',
  );
  return partialCredit ? partialCredit.students.length : 0;
};

export const sortQuestionsByResults = (
  questions,
  learnosityReportsByItemData,
  students,
) => {
  const questionResultsStacks = questions.reduce((accum, item) => {
    const isFRQItem = isFRQ(item);
    const currentTiers = isFRQItem ? questionFRQTiers : questionTiers;
    return {
      ...accum,
      [item.learnosityItemReference]: getPerformanceStacks(
        item.learnosityItemReference,
        learnosityReportsByItemData,
        currentTiers,
        students,
      ),
    };
  }, {});

  questions.sort((questionA, questionB) => {
    const questionAStacks =
      questionResultsStacks[questionA.learnosityItemReference];
    const questionBStacks =
      questionResultsStacks[questionB.learnosityItemReference];
    const fullCreditA = getFullCreditNumber(questionAStacks);
    const fullCreditB = getFullCreditNumber(questionBStacks);

    let comparation = fullCreditB - fullCreditA;
    if (comparation === 0) {
      const partialCreditA = getPartialCreditNumber(questionAStacks);
      const partialCreditB = getPartialCreditNumber(questionBStacks);
      comparation = partialCreditB - partialCreditA;
    }
    if (comparation === 0) {
      return questions.indexOf(questionA) - questions.indexOf(questionB);
    }
    return comparation;
  });
  return questions;
};

export const sortQuestionsByResultsForStudent = (
  questions,
  learnosityReportsByItemData,
  students,
  studentId,
) => {
  const questionResultsPercent = questions.reduce((accum, item) => {
    const isFRQItem = isFRQ(item);
    const currentTiers = isFRQItem ? questionFRQTiers : questionTiers;
    const stacks = getPerformanceStacks(
      item.learnosityItemReference,
      learnosityReportsByItemData,
      currentTiers,
      students,
    );
    const stackWithCurrentStudent = stacks.find(
      (stack) =>
        stack.students &&
        stack.students.some((student) => student.userId === studentId),
    );
    if (stackWithCurrentStudent) {
      const currentStudent = stackWithCurrentStudent.students.find(
        (student) => student.userId === studentId,
      );
      //Fill question-id -> percent dictionary for current student
      return {
        ...accum,
        [item.learnosityItemReference]: currentStudent.percentCorrect || 0,
      };
    }
    return accum;
  }, {});

  questions.sort((questionA, questionB) => {
    const comparation =
      questionResultsPercent[questionB.learnosityItemReference] -
      questionResultsPercent[questionA.learnosityItemReference];
    if (comparation === 0) {
      return questions.indexOf(questionA) - questions.indexOf(questionB);
    }
    return comparation;
  });
  return questions;
};

export const sortQuestionsBySkills = (questions, isDescending) => {
  const skillsByQuestionId = zipObject(
    questions.map((question) => question.id),
    questions.map((question) =>
      (getQuestionSkills(question, false) || []).map((skill) => skill.label),
    ),
  );
  questions.sort((questionA, questionB) => {
    const aSkills = skillsByQuestionId[questionA.id];
    const bSkills = skillsByQuestionId[questionB.id];
    // if skill set are the same, second sort criteria should
    // always be ASCENDING question number order
    if (isEqual(aSkills, bSkills)) {
      return isDescending
        ? questionB.idx - questionA.idx
        : questionA.idx - questionB.idx;
    }
    return (
      zip(aSkills, bSkills)
        .map(([aSkill, bSkill]) =>
          (aSkill || '').localeCompare(bSkill || '', undefined, {
            numeric: 1,
            sensitivity: 'base',
          }),
        )
        .find((comparison) => comparison !== 0) || 0
    );
  });
  return questions;
};

export const sortQuestionsByOtherTags = (questions, isDescending, sortId) => {
  let tagsByQuestionId;
  if (sortId === 'units') {
    tagsByQuestionId = zipObject(
      questions.map((question) => question.id),
      questions.map((question) =>
        (getQuestionUnits(question, false) || []).map((tag) => tag.label),
      ),
    );
  } else if (sortId === 'topics') {
    tagsByQuestionId = zipObject(
      questions.map((question) => question.id),
      questions.map((question) =>
        (getQuestionTopics(question, false) || []).map((tag) => tag.label),
      ),
    );
  } else if (sortId === 'taskModels') {
    tagsByQuestionId = zipObject(
      questions.map((question) => question.id),
      questions.map((question) =>
        (getQuestionTaskModels(question, false) || []).map((tag) => tag.label),
      ),
    );
  } else {
    tagsByQuestionId = zipObject(
      questions.map((question) => question.id),
      questions.map((question) =>
        (getQuestionProficiencies(question, false) || []).map(
          (tag) => tag.label,
        ),
      ),
    );
  }
  questions.sort((questionA, questionB) => {
    const aTags = tagsByQuestionId[questionA.id];
    const bTags = tagsByQuestionId[questionB.id];
    // if skill set are the same, second sort criteria should
    // always be ASCENDING question number order
    if (isEqual(aTags, bTags)) {
      return isDescending
        ? questionB.idx - questionA.idx
        : questionA.idx - questionB.idx;
    }
    return (
      zip(aTags, bTags)
        .map(([aTag, bTag]) =>
          (aTag || '').localeCompare(bTag || '', undefined, {
            numeric: 1,
            sensitivity: 'base',
          }),
        )
        .find((comparison) => comparison !== 0) || 0
    );
  });
  return questions;
};

/**
 * Find references within a list of questions
 * If question has children (parts) return reference from parts, if not, return question reference
 * @params {array} questions List of questions
 * @returns {array} List of references
 * */
export const getReferencesFromQuestions = (questions) => {
  let references = [];
  questions.forEach(
    ({
      learnosityItemReference: questionReference,
      id: questionId,
      children,
    }) => {
      if ((children || []).length > 0) {
        children.forEach(
          ({ learnosityItemReference: partReference, id: partId }) => {
            references.push({
              learnosity_item_reference: partReference,
              item_id: partId,
            });
          },
        );
      } else {
        references.push({
          learnosity_item_reference: questionReference,
          item_id: questionId,
        });
      }
    },
  );
  return references;
};

/**
 * Find first question within a list of questions
 * @params {array} questions List of questions
 * @returns {array} First question
 * */
export const getFirstQuestion = (questions) => {
  const { id, learnosityItemReference } = (questions || [])[0] || {};
  return {
    learnosity_item_reference: learnosityItemReference,
    item_id: id,
  };
};

export const getSPPPartsTipsData = (
  report,
  students = [],
  studentResponses,
  submissionData = [],
  sessionTemplate,
) => {
  const reportsData = get(report[0], 'data', {});
  const activityIDs = Object.keys(reportsData);
  const activityReport =
    activityIDs.length === 1 ? reportsData[activityIDs[0]] : {};
  const reportByUser = get(activityReport, 'users', {});
  const studentQuestionResponses = students.map((student) => {
    if (studentResponses && studentResponses[student.id]) {
      return {
        student,
        responses: studentResponses[student.id].questions,
      };
    }
  });
  const getStudentProgressPerecentage = (userReport) => {
    const { score, max_score: maxScore } = userReport;
    const progressPercentage = Math.round((score / maxScore) * 100);
    return progressPercentage;
  };
  const studentProgressData = (responseId, submissionData) => {
    let responseObj = {
      studentsNotStarted: [],
      studentsNotSubmitted: [],
      studentsSubmitted: [],
    };
    const studentHasAnswer = (studentId) => {
      const studentResponded = studentQuestionResponses.find((response) => {
        return (
          get(response, 'responses') &&
          response.responses[responseId] &&
          response.student.id.toString() === studentId.toString()
        );
      });
      return studentResponded;
    };
    submissionData.forEach((student) => {
      const userReport = get(reportByUser, student.user_id, null);
      let studentWithProgress = 0;
      if (!student.submitted && studentHasAnswer(student.user_id)) {
        if (userReport) {
          studentWithProgress = getStudentProgressPerecentage(userReport);
        }
        responseObj.studentsNotSubmitted.push({
          ...student,
          studentWithProgress,
        });
      } else if (student.submitted && studentHasAnswer(student.user_id)) {
        if (userReport) {
          studentWithProgress = getStudentProgressPerecentage(userReport);
        }
        responseObj.studentsSubmitted.push({ ...student, studentWithProgress });
      } else {
        responseObj.studentsNotStarted.push(student);
      }
    });
    return responseObj;
  };
  const answerTipsData = get(sessionTemplate, 'items', []).map((item) => {
    const partObj = {
      reference: item.reference,
      part: {},
      tips: [],
    };
    get(item, 'questions', []).forEach((question) => {
      if (question.type === 'longtextV2') {
        partObj.part = {
          responseId: question.response_id,
          ...studentProgressData(question.response_id, submissionData),
          totalStudents: students.length,
        };
      } else if (question.type === 'formulaessayV2') {
        partObj.tips.push({
          responseId: question.response_id,
          ...studentProgressData(question.response_id, submissionData),
          totalStudents: students.length,
        });
      } else if (question.type === 'clozedropdown') {
        partObj.tips.push({
          responseId: question.response_id,
          ...studentProgressData(question.response_id, submissionData),
          totalStudents: students.length,
        });
      }
    });
    return partObj;
  });

  return answerTipsData || [];
};

export const mapLearnosityItemReferenceToaverageTimeSpentInSeconds = (
  normalizedQuestionData = [],
) => {
  const learnosityItemReferenceToaverageTimeSpentInSecondsMap = {};
  normalizedQuestionData.forEach(
    ({ learnosityItemReference, averageTimeSpentInSeconds }) => {
      learnosityItemReferenceToaverageTimeSpentInSecondsMap[
        learnosityItemReference
      ] = averageTimeSpentInSeconds;
    },
  );
  return learnosityItemReferenceToaverageTimeSpentInSecondsMap;
};

export const sortQuestionsByAverageTimeSpent = (
  questionsWithNumbers = [],
  learnosityItemReferenceToaverageTimeSpentInSecondsMap = {},
  isDescending = false,
) => {
  const sortedData = sortBy(questionsWithNumbers, (question) => {
    const averageTimeSpentInSeconds =
      learnosityItemReferenceToaverageTimeSpentInSecondsMap[
        question.learnosityItemReference
      ];
    return isDescending
      ? -averageTimeSpentInSeconds
      : averageTimeSpentInSeconds;
  });
  return sortedData;
};
